import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button } from '@backstage/ui';
import { Combobox } from '@ns/plugin-ns-theme/combobox';

import { filterByLabel, toChoiceOptions } from './choice-options';
import type { ChoiceState } from './choice-state';

const LOADING_TEXT = 'Loading…';

/**
 * Whether an open list may stay open while it has no options.
 *
 * @remarks
 * Only an answer keeps an empty list open: an empty `ready` answer says there
 * is nothing, and a standing list filtered to nothing says nothing matched.
 * Without rows, `fetching`, `failed` and `awaiting` have nothing true to show
 * in the list, so it closes and the failure next to the field is reachable.
 * A remote search is the exception while a new answer can still come: during
 * `fetching`, and after a non-fatal failure once the input differs from the
 * term that failed, so typing a new term opens the list at once.
 */
function keepsEmptyListOpen(
  state: ChoiceState,
  optionCount: number,
  hasRemoteSearch: boolean,
  inputValue: string,
  failedTerm: string | null,
): boolean {
  if (state.kind === 'ready' || optionCount > 0) return true;
  if (!hasRemoteSearch || state.kind === 'awaiting') return false;
  if (state.kind !== 'failed') return true;
  return state.severity !== 'fatal' && inputValue !== failedTerm;
}

/**
 * Whether a remote search keeps the input usable while there is nothing to
 * choose, so the filler can type the next term.
 *
 * @remarks
 * That holds while a new answer can still come: during `fetching` and after a
 * non-fatal failure. A fatal failure and `awaiting` keep the input disabled,
 * because typing cannot put either right.
 */
function acceptsNewTerm(state: ChoiceState, hasRemoteSearch: boolean): boolean {
  if (!hasRemoteSearch) return false;
  if (state.kind === 'fetching') return true;
  return state.kind === 'failed' && state.severity !== 'fatal';
}

/**
 * The input text at the render where `state` turned `failed`, or `null` while
 * it is not `failed`.
 *
 * @remarks
 * It tells the term that failed apart from a new one typed after it. The
 * value is adjusted during render rather than in an effect, so the very render
 * in which the failure arrives already sees it and closes the list.
 *
 * The capture resets only when `state` leaves `failed`. So a provider must pass
 * through `fetching` for a new term before that term can fail. A provider
 * that reports `failed` for one term and then `failed` for a new term, with no
 * other state in between, is not supported: the list would stay open on
 * "Loading…". A TanStack Query provider meets this, because a new query key
 * reports `fetching` in the very render the key changes.
 */
function useFailedTerm(state: ChoiceState, inputValue: string): string | null {
  const [captured, setCaptured] = useState<string | null>(null);
  const isFailed = state.kind === 'failed';
  if (isFailed && captured === null) setCaptured(inputValue);
  if (!isFailed && captured !== null) setCaptured(null);
  return isFailed ? captured ?? inputValue : null;
}

/**
 * The text an open list shows when it has no options.
 *
 * @remarks
 * Without options the text follows the state, so a failure never reads as
 * "nothing found": the failure message for the term that failed, and
 * "Loading…" while an answer is on its way, including a new term typed after
 * a failure. With options that were all filtered away it says nothing matched.
 */
function describeEmptyList(
  state: ChoiceState,
  optionCount: number,
  inputValue: string,
  failedTerm: string | null,
  emptyText: string,
): string {
  if (optionCount === 0 && state.kind === 'fetching') return LOADING_TEXT;
  if (optionCount === 0 && state.kind === 'failed') {
    return inputValue === failedTerm ? state.message : LOADING_TEXT;
  }
  return inputValue.length === 0
    ? emptyText
    : `Nothing found for "${inputValue}"`;
}

/**
 * The description shown under the closed field before a lapse message
 * overrides it, or `undefined` to fall back to the template's own
 * description.
 *
 * @remarks
 * Extracted so the state-to-text mapping does not add to the component's own
 * cognitive-complexity budget.
 */
function describeState(
  state: ChoiceState,
  emptyText: string,
  hasItems: boolean,
  optionCount: number,
  inputLength: number,
): string | undefined {
  if (state.kind === 'awaiting')
    return `Choose ${state.missing.join(', ')} first`;
  if (state.kind === 'fetching') return LOADING_TEXT;
  const isEmptyReadyAnswer =
    state.kind === 'ready' &&
    hasItems &&
    optionCount === 0 &&
    inputLength === 0;
  return isEmptyReadyAnswer ? emptyText : undefined;
}

export interface ChoiceFieldProps<TItem> {
  /** The template's description, shown while the field has no message of its own. */
  description?: string;
  /**
   * Text for an empty list when nothing was typed. Defaults to "No results
   * found.".
   */
  emptyText?: string;
  getKey: (item: TItem) => string | undefined;
  getLabel: (item: TItem) => string | undefined;
  /**
   * The rows to offer, or `undefined` when there is no answer yet.
   *
   * @remarks
   * Absent is not the same as empty. An empty list DISPROVES a selection, so it
   * lapses; an absent one proves nothing, so a selection that was valid stays
   * valid while a request is in flight.
   */
  items?: readonly TItem[];
  label: string;
  onSelect: (item: TItem | undefined) => void;
  /**
   * Lets a provider own searching and state whether its current answer can
   * authoritatively disprove the selected item.
   *
   * @remarks
   * Omit this object to retain local label filtering. When supplied, the
   * provider receives every raw input value and its returned rows are rendered
   * without a second local filter. Both members are required so a caller cannot
   * accidentally enable remote mode without selection evidence.
   */
  remoteSearch?: {
    /** Whether the current provider answer is complete enough to lapse a missing selection. */
    readonly mayDisproveSelection: boolean;
    /** Receives the raw combobox input value whenever the user changes it. */
    readonly onInputValueChange: (value: string) => void;
  };
  required?: boolean;
  selectedKey: string | null;
  /** The label of the current value, so a message can name what was dropped. */
  selectedLabel: string | null;
  /**
   * Identifies the question these items answer, so an answer to a DIFFERENT
   * question never keeps a selection alive.
   *
   * @remarks
   * A picker builds this out of whatever determines its list: the dependency
   * values it resolved, or the query key it fetched under. When it changes, the
   * selection lapses even if the same key reappears in the new list, because it
   * was chosen against a question that is no longer being asked.
   */
  sourceKey?: string;
  state: ChoiceState | (() => ChoiceState);
}

/**
 * The shared choice field over the `ns-theme` `Combobox`.
 *
 * @remarks
 * It turns a `ChoiceState` plus a list of raw items into a rendered combobox,
 * and drops a selection that no longer makes sense, announcing what was dropped
 * until the filler picks again.
 *
 * The rule it enforces is one invariant rather than a branch per state: a valid
 * combination of items and selection stays valid, INCLUDING while a new answer
 * is on its way. Only three things can disprove a selection, and each is
 * checked wherever it can happen: the question changed (`sourceKey`), the
 * option vanished from an answer that actually arrived (`items`), or the field
 * went back to waiting on another field (`awaiting`).
 */
export function ChoiceField<TItem>(
  props: ChoiceFieldProps<TItem>,
): React.ReactElement {
  const {
    description: templateDescription,
    emptyText = 'No results found.',
    getKey,
    getLabel,
    items,
    label,
    onSelect,
    remoteSearch,
    required = false,
    selectedKey,
    selectedLabel,
    sourceKey,
    state: stateProp,
  } = props;

  const state = typeof stateProp === 'function' ? stateProp() : stateProp;

  // Written as a variable, not `onSelect(undefined)` directly: a literal
  // `undefined` argument reads as redundant to the linter even though the
  // parameter is required, not optional.
  const clearSelection = useCallback(() => {
    const cleared: TItem | undefined = undefined;
    onSelect(cleared);
  }, [onSelect]);

  // Each ChoiceField instance owns its typed text — two instances on the same
  // form never share a keystroke.
  const [inputValue, setInputValue] = useState('');
  // The label of a selection this field just dropped on its own initiative,
  // shown as `description` until the filler picks again. `selectedLabel`
  // itself goes blank the moment `onSelect(undefined)` clears the value, so
  // the dropped label has to be captured before that happens.
  const [lapsedLabel, setLapsedLabel] = useState<string | null>(null);

  const failedTerm = useFailedTerm(state, inputValue);

  // Options are built whatever the state is, so a list that already stood keeps
  // standing through a refetch and through a non-fatal failure.
  // `items ?? []` is load-bearing: `toChoiceOptions` iterates, and an
  // absent list is not an error here, it is "no answer yet".
  const { notices, options } = toChoiceOptions(items ?? [], getKey, getLabel);
  const visibleOptions =
    remoteSearch === undefined ? filterByLabel(options, inputValue) : options;
  // Joined on a character no option key can contain, so the effect below can
  // compare the list as a string without a key containing the separator
  // splitting it.
  const optionKeys = options.map(option => option.key).join('\u0000');

  // Reflects an externally-bound selection (the form loaded with an existing
  // value, or a lapse just cleared it) into the input text. Keyed on
  // `selectedKey` via a ref so it fires only on a fresh binding, never while
  // the user is typing (typing moves `inputValue`, not `selectedKey`).
  const hydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (selectedKey === hydratedKeyRef.current) return;
    hydratedKeyRef.current = selectedKey;
    setInputValue(selectedKey === null ? '' : selectedLabel ?? '');
  }, [selectedKey, selectedLabel]);

  // The `sourceKey` this field last validated against. `undefined` means "not
  // seen yet", which covers the first render after mounting: there is no
  // earlier question to compare against. A wizard remounts a step's fields on
  // Back and Next, so the list check below still runs on that first render.
  const previousSourceKeyRef = useRef<string | undefined>(undefined);

  const stateKind = state.kind;
  const hasItems = items !== undefined;

  useEffect(() => {
    // Going back to waiting on another field always lapses the choice: the
    // answer it was chosen from is no longer being asked for.
    if (stateKind === 'awaiting') {
      if (selectedKey !== null) {
        setLapsedLabel(selectedLabel);
        clearSelection();
      }
      return;
    }

    const previousSourceKey = previousSourceKeyRef.current;
    previousSourceKeyRef.current = sourceKey;

    if (selectedKey === null) return;

    const sourceChanged =
      previousSourceKey !== undefined && previousSourceKey !== sourceKey;

    // Only an answer that actually arrived can disprove a selection. While
    // `items` is absent the selection is untouched, which is what keeps it
    // alive across a refetch.
    const selectionMissing =
      hasItems &&
      (remoteSearch === undefined || remoteSearch.mayDisproveSelection) &&
      !optionKeys.split('\u0000').includes(selectedKey);

    if (sourceChanged || selectionMissing) {
      setLapsedLabel(selectedLabel);
      clearSelection();
    }
  }, [
    clearSelection,
    hasItems,
    optionKeys,
    selectedKey,
    selectedLabel,
    sourceKey,
    stateKind,
    remoteSearch,
  ]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      remoteSearch?.onInputValueChange(value);
    },
    [remoteSearch],
  );

  const isFatal = state.kind === 'failed' && state.severity === 'fatal';
  // Selectability hangs on the LIST, never on `severity`. `severity` answers
  // two gates joined by OR, and the second ("nothing left to put right") can
  // fire while a perfectly good list is on screen: a withdrawn consent is fatal
  // on an empty field and no obstacle at all to a filler who can already see
  // his thirty groups. Letting `fatal` close the list would contradict the rule that
  // keeps a standing list precisely so the filler does not also lose what he
  // DID have. So `severity` drives only the Alert's status and, later, whether
  // submitting is blocked, not what can be chosen.
  //
  // `ready` with an empty list is the one case where there is nothing to choose
  // and the field stays reachable anyway: an empty answer is a real answer, and
  // The field has to be openable to say so ("no fault marking, and a
  // text saying there is nothing"). An answer that never arrived says nothing,
  // so `fetching` and an absent list leave nothing to choose.
  const providerRefresh =
    remoteSearch !== undefined && state.kind === 'fetching' && hasItems;
  const selectable =
    providerRefresh ||
    (state.kind !== 'fetching' &&
      (state.kind === 'ready' || options.length > 0));
  // `selectable` still decides what can be chosen, and a remote search can
  // keep the input usable without it (see `acceptsNewTerm`).
  const isInputEnabled =
    selectable || acceptsNewTerm(state, remoteSearch !== undefined);
  // The rows the Combobox really shows: none while nothing can be chosen.
  const shownOptionCount = selectable ? options.length : 0;
  const allowsEmptyCollection = keepsEmptyListOpen(
    state,
    shownOptionCount,
    remoteSearch !== undefined,
    inputValue,
    failedTerm,
  );

  const handleSelectionChange = useCallback(
    (key: string | null) => {
      setLapsedLabel(null);
      hydratedKeyRef.current = key;
      if (key === null) {
        setInputValue('');
        clearSelection();
        return;
      }
      const selected = options.find(option => option.key === key);
      // react-aria commits the current key again on blur and on Enter. While
      // nothing can be chosen there is no list that could disprove it, so the
      // selection stays and the input shows its label again.
      if (selected === undefined && key === selectedKey && !selectable) {
        setInputValue(selectedLabel ?? '');
        return;
      }
      setInputValue(selected?.label ?? '');
      onSelect(selected?.item);
    },
    [clearSelection, onSelect, options, selectable, selectedKey, selectedLabel],
  );

  const stateDescription =
    describeState(
      state,
      emptyText,
      hasItems,
      options.length,
      inputValue.length,
    ) ?? templateDescription;
  const description =
    lapsedLabel === null
      ? stateDescription
      : `The earlier choice "${lapsedLabel}" no longer applies`;

  const comboboxEmptyText = describeEmptyList(
    state,
    shownOptionCount,
    inputValue,
    failedTerm,
    emptyText,
  );

  // The field's own notice sits WITH the list rather than under the field
  // so it joins the ones the option list produced about what it left out.
  const stateNotices =
    state.kind === 'ready' && state.notice !== undefined
      ? [state.notice].flat()
      : [];
  const allNotices = [...notices, ...stateNotices];
  const hint = allNotices.length > 0 ? allNotices.join('; ') : undefined;

  const comboboxOptions = selectable
    ? visibleOptions.map(option => ({
        disabled: providerRefresh,
        label: option.label,
        value: option.key,
      }))
    : [];

  return (
    <>
      <Combobox
        allowsEmptyCollection={allowsEmptyCollection}
        description={description}
        emptyText={comboboxEmptyText}
        hint={hint}
        inputValue={inputValue}
        isDisabled={!isInputEnabled}
        isInvalid={isFatal}
        isLoading={state.kind === 'fetching'}
        isRequired={required}
        label={label}
        onInputChange={handleInputChange}
        onSelectionChange={handleSelectionChange}
        options={comboboxOptions}
        selectedKey={selectedKey}
      />
      {state.kind === 'failed' && (
        <Alert
          customActions={
            state.retry && (
              <Button onPress={state.retry} size="small">
                Try again
              </Button>
            )
          }
          description={state.description}
          status={state.severity === 'fatal' ? 'danger' : 'warning'}
          title={state.message}
        />
      )}
    </>
  );
}
